// i18n configuration for Wefaqna web (Phase 3.B — 2026-06-23).
//
// Strategy:
//   - Auto-detect from browser language (navigator.language) on first load
//   - User can override in Settings → Language (stored in localStorage)
//   - Arabic is the default fallback for anything not matched
//   - Missing translation keys return their KEY name (never crash; obvious flag for translators)
//
// Locale matrix:
//   ar    — full coverage (existing app default)
//   en    — full coverage (Phase 3.B target)
//   fr    — shipped 2026-08-21
//   de    — shipped 2026-08-24 (Germany is our largest non-Arabic install base)
//   es    — shipped 2026-10-08
//   tr, id, ur — shipped 2026-10-23
//
// Only Arabic and English are in the main bundle: they are the end of every
// fallback chain and 96% of visitors read one of them. Every other language
// is its own chunk, fetched before the first paint for the visitor who needs
// it (`i18nReady`) and by `changeLanguage` for one who switches. Eight
// catalogues of ~350 KB each in one bundle is what every visitor would
// otherwise download to read two.
//
// Why react-i18next over react-intl:
//   - Simpler key-based lookups (no ICU MessageFormat overhead for basic strings)
//   - Async loading out of the box (we may split locales later for bundle size)
//   - Industry-standard in 2026 with the widest community support
//
// Why Arabic default (not English):
//   - 96% of users in 2026 are Arabic-locale; flipping the default would
//     ship-break the existing UX
//   - i18next-browser-languagedetector picks navigator.language FIRST, so
//     ar-locale users get Arabic naturally; only diaspora gets English

import i18n from "i18next";
import { initReactI18next } from "react-i18next";
import LanguageDetector from "i18next-browser-languagedetector";

import { safeLocalStorage } from "../utils/safeStorage";

import ar from "../locales/ar.json";
import en from "../locales/en.json";
import { SUPPORTED_LANGUAGES } from "./languages";

export { SUPPORTED_LANGUAGES };

/** The catalogues bundled into the main chunk (see the header). */
const BUNDLED = { ar, en };

/**
 * An i18next backend over webpack chunks. A bundled language (ar, en) answers
 * with nothing to add, and so does a code we do not ship. A failed fetch
 * reports an error, and i18next then renders the fallback chain - English -
 * rather than nothing.
 */
const chunkBackend = {
  type: "backend",
  init() {},
  read(language, _namespace, callback) {
    if (BUNDLED[language] || !SUPPORTED_LANGUAGES.includes(language)) {
      callback(null, {});
      return;
    }
    import(/* webpackChunkName: "locale-[request]" */ `../locales/${language}.json`)
      .then((module) => callback(null, module.default || module))
      .catch((error) => callback(error, null));
  },
};

// Languages that should render as RTL.
export const RTL_LANGUAGES = ["ar", "fa", "ur", "he"];

export function isRtl(lng) {
  if (!lng) return false;
  // Strip region code (ar-EG → ar) before checking the RTL set.
  const base = lng.toLowerCase().split(/[-_]/)[0];
  return RTL_LANGUAGES.includes(base);
}

// Apply <html dir> and <html lang> based on the active locale. Keeping this
// reactive to language changes is what makes the RTL/LTR flip work
// instantly when the user changes language in Settings.
export function applyDocumentDirection(lng) {
  if (typeof document === "undefined") return;
  document.documentElement.lang = lng || "ar";
  document.documentElement.dir = isRtl(lng) ? "rtl" : "ltr";
  applyManifestLanguage(lng);
}

/** Languages that have their own `public/manifest.<lang>.json`. */
const MANIFEST_LANGUAGES = SUPPORTED_LANGUAGES;

/**
 * Point <link rel="manifest"> at the manifest for this language.
 *
 * A web app manifest carries no i18n — `lang` only declares what language its
 * single set of strings is written in. So "Add to Home Screen" offered the
 * Arabic app name and description to every visitor, whatever language the page
 * around it was in.
 *
 * Wrapped in try/catch and guarded on the element existing: a missing or
 * unwritable <link> must never be able to break rendering, and the base
 * manifest is still perfectly valid if this does nothing.
 */
export function applyManifestLanguage(lng) {
  try {
    const link = document.querySelector('link[rel="manifest"]');
    if (!link) return;
    const base = (lng || "ar").toLowerCase().split(/[-_]/)[0];
    const lang = MANIFEST_LANGUAGES.includes(base) ? base : "ar";
    const href = `/manifest.${lang}.json`;
    if (!link.getAttribute("href")?.endsWith(href)) {
      link.setAttribute("href", href);
    }
  } catch {
    /* A manifest swap is a nicety; never let it take the page down. */
  }
}

// The detector's own "localStorage" source reads window.localStorage DIRECTLY.
// A browser set to block site data throws on that access — not on read, on the
// property itself — and because this file calls .init() at import time the throw
// lands during module bootstrap, before any error boundary exists:
//
//     SecurityError: The operation is insecure.
//     at require(webpack/bootstrap)
//
// It is the same class as WEFAQNA-FRONTEND-1, the first error web Sentry ever
// saw, which safeStorage was written to end: our own code was routed through
// the wrapper, but this third-party detector was not, so the hole stayed open
// in the one place that runs earliest.
//
// CORRECTION (2026-09-16). This comment used to credit that change with
// closing WEFAQNA-FRONTEND-D — 66 events across 12 members. It did not, and
// two things say so. The stack on those events runs through
// `media-encoder-host`, i.e. the voice RECORDER, not a language detector; and
// the issue last fired on 11 Sep while this fix shipped on 14 Sep, so it had
// already stopped. What actually closed it was `2c16b8b5`, which made
// VoiceRecorder lazy and capability-gated so the encoder never loads on
// iOS 12.5.
//
// The change below is still worth having — the detector really did read
// window.localStorage directly, and that really does throw where site data is
// blocked. It is a hole closed on principle, not a fix with a body count.
//
// Registering a custom source backed by safeLocalStorage closes it. Behaviour is
// unchanged where storage works; where it is blocked, lookup returns undefined
// and detection falls through to `navigator` — which is the right answer anyway,
// since a member who blocks storage has no saved choice to honour.
const safeLocalStorageDetector = {
  name: "safeLocalStorage",
  lookup({ lookupLocalStorage }) {
    return safeLocalStorage.getItem(lookupLocalStorage) || undefined;
  },
  cacheUserLanguage(lng, { lookupLocalStorage }) {
    safeLocalStorage.setItem(lookupLocalStorage, lng);
  },
};

const languageDetector = new LanguageDetector();
languageDetector.addDetector(safeLocalStorageDetector);

/**
 * Resolves once the visitor's language is loaded. `index.js` awaits it before
 * the first render, so a Turkish visitor never sees a frame of English.
 */
export const i18nReady = i18n
  .use(languageDetector)
  .use(chunkBackend)
  .use(initReactI18next)
  .init({
    resources: {
      ar: { translation: ar },
      en: { translation: en },
    },
    // ar and en come from `resources`; everything else from `chunkBackend`.
    partialBundledLanguages: true,
    // PER-LANGUAGE fallback chain, not a single global value.
    //
    // This was `fallbackLng: "ar"`, which is right for an English user (the
    // audience is Arabic-speaking) and completely wrong for a French one: any
    // key not yet translated would have rendered ARABIC to somebody who came
    // in through a French store listing. French must fall back to English —
    // which is exactly what French users see today, so an untranslated string
    // can never be a regression.
    fallbackLng: {
      fr: ["en", "ar"],
      de: ["en", "ar"],
      es: ["en", "ar"],
      tr: ["en", "ar"],
      id: ["en", "ar"],
      ur: ["en", "ar"],
      default: ["ar"],
    },
    supportedLngs: SUPPORTED_LANGUAGES,
    nonExplicitSupportedLngs: true, // Map ar-EG → ar, en-US → en, etc.
    detection: {
      // Order: user's explicit choice (localStorage) wins; then browser; then HTML lang.
      // "safeLocalStorage", not "localStorage" — see the detector above. The
      // key name is unchanged, so a member's existing saved choice still reads.
      order: ["safeLocalStorage", "navigator", "htmlTag"],
      lookupLocalStorage: "wefaqna_language",
      caches: ["safeLocalStorage"],
    },
    interpolation: {
      // React already escapes — don't double-escape.
      escapeValue: false,
    },
    react: {
      // Avoid Suspense fallback on first paint; we always have resources loaded.
      useSuspense: false,
    },
    // When a key is missing, follow the chain above (fr/de → en → ar,
    // everything else → ar), then return the key name (never crash, never
    // empty render).
    returnEmptyString: false,
    returnNull: false,
  });

// Apply direction on init + every language change.
applyDocumentDirection(i18n.language);
i18n.on("languageChanged", applyDocumentDirection);

export default i18n;
